import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isYdelserOpen, setIsYdelserOpen] = useState(false)
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const dropdownTimeoutRef = useRef(null)

  // Close mobile menu and desktop dropdown on route change
  useEffect(() => {
    setIsOpen(false)
    setIsDesktopDropdownOpen(false)
  }, [location.pathname])

  // Track scroll position to make navbar solid when sticky
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    setIsDesktopDropdownOpen(true)
  }

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDesktopDropdownOpen(false)
    }, 150) // 150ms delay for smooth hover experience
  }

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const navLinks = [
    { name: 'Forside', path: '/' },
    { name: 'Om os', path: '/om-os' },
    {
      name: 'Ydelser',
      path: '#',
      isDropdown: true,
      subLinks: [
        { name: 'Porebeton', path: '/porebeton' },
        { name: 'Renovering', path: '/renovering' },
        { name: 'Nybyg', path: '/nybyg' },
      ]
    },
    { name: 'Kontakt', path: '/kontakt' },
  ]

  const isActive = (link) => {
    if (link.isDropdown) {
      return link.subLinks.some(sub => location.pathname === sub.path)
    }
    if (link.path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(link.path)
  }

  return (
    <header className={`sticky top-0 z-50 w-full border-b transition-all duration-200 ${
      isScrolled 
        ? 'bg-white border-warm-gray-200 shadow-md' 
        : 'bg-white/95 backdrop-blur-md border-warm-gray-200'
    }`}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Typographic Logo */}
        <Link to="/" className="flex flex-col group">
          <span className="text-xl sm:text-2xl font-extrabold tracking-tight text-anthracite transition-colors leading-tight">
            KRISTENSEN
          </span>
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-warm-gray-500 leading-none mt-1">
            BYG & MONTAGE ApS
          </span>
        </Link>

        {/* Desktop Navigation - Only visible at 1200px and above */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {navLinks.map((link) => {
            if (link.isDropdown) {
              const active = isActive(link)
              return (
                <div
                  key={link.name}
                  className="relative py-2"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`flex items-center gap-1.5 text-sm font-semibold tracking-wider uppercase transition-colors duration-200 cursor-pointer ${
                      active
                        ? 'text-black font-bold'
                        : 'text-warm-gray-600 hover:text-black'
                    }`}
                  >
                    {link.name}
                    <i className={`fa-light fa-chevron-down text-xs transition-transform duration-200 ${isDesktopDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDesktopDropdownOpen && (
                    <div className="absolute left-0 top-full w-48 rounded-md bg-white py-2 shadow-lg border border-warm-gray-200 animate-in fade-in slide-in-from-top-1 duration-150">
                      {link.subLinks.map((sub) => {
                        const subActive = location.pathname === sub.path
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={`block px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-150 ${
                              subActive
                                ? 'bg-warm-gray-50 text-black font-extrabold'
                                : 'text-warm-gray-600 hover:bg-warm-gray-50 hover:text-black'
                            }`}
                          >
                            {sub.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            const active = isActive(link)
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative py-2 text-sm font-semibold tracking-wider uppercase transition-colors duration-200 ${
                  active
                    ? 'text-black font-bold'
                    : 'text-warm-gray-600 hover:text-black'
                }`}
              >
                {link.name}
              </Link>
            )
          })}
          <Link
            to="/kontakt"
            className="ml-4 rounded bg-brand-green px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200"
          >
            få et tilbud
          </Link>
        </nav>

        {/* Mobile Menu Button - Visible below 1200px */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-[1200px]:hidden items-center justify-center p-2 text-warm-gray-600 hover:text-brand-green focus:outline-none"
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <i className={`fa-light ${isOpen ? 'fa-xmark text-2xl' : 'fa-bars text-xl'}`} />
        </button>
      </div>

      {/* Mobile Drawer - Slide down menu below 1200px */}
      {isOpen && (
        <div className="fixed inset-0 top-20 z-40 min-[1200px]:hidden bg-warm-gray-900/40 backdrop-blur-sm transition-opacity duration-300">
          <div className="absolute right-0 top-0 w-full max-w-sm bg-white h-[calc(100vh-5rem)] shadow-xl flex flex-col p-6 border-l border-warm-gray-200 animate-in slide-in-from-right duration-200">
            <div className="flex flex-col gap-3 mt-4">
              {navLinks.map((link) => {
                if (link.isDropdown) {
                  const active = isActive(link)
                  return (
                    <div key={link.name} className="flex flex-col">
                      <button
                        onClick={() => setIsYdelserOpen(!isYdelserOpen)}
                        className={`flex items-center justify-between py-3 px-4 rounded-lg text-base font-semibold tracking-wider uppercase transition-all duration-200 cursor-pointer ${
                          active
                            ? 'bg-warm-gray-50 text-black border-l-4 border-black pl-3'
                            : 'text-warm-gray-700 hover:bg-warm-gray-50 hover:text-black'
                        }`}
                      >
                        {link.name}
                        <i className={`fa-light fa-chevron-down text-xs transition-transform duration-200 ${isYdelserOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Sublinks */}
                      {isYdelserOpen && (
                        <div className="flex flex-col gap-1 pl-6 pr-4 py-2 bg-warm-gray-50/50 rounded-b-lg border-l-4 border-warm-gray-200/50 ml-4 mt-1">
                          {link.subLinks.map((sub) => {
                            const subActive = location.pathname === sub.path
                            return (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                className={`flex items-center justify-between py-2.5 px-3 rounded text-sm font-semibold tracking-wider uppercase transition-all duration-150 ${
                                  subActive
                                    ? 'text-black font-bold'
                                    : 'text-warm-gray-600 hover:text-black'
                                }`}
                              >
                                {sub.name}
                                <i className="fa-light fa-chevron-right text-[10px] opacity-40" />
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                }

                const active = isActive(link)
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center justify-between py-3 px-4 rounded-lg text-base font-semibold tracking-wider uppercase transition-all duration-200 ${
                      active
                        ? 'bg-warm-gray-50 text-black border-l-4 border-black pl-3'
                        : 'text-warm-gray-700 hover:bg-warm-gray-50 hover:text-black'
                    }`}
                  >
                    {link.name}
                    <i className="fa-light fa-chevron-right text-xs opacity-50" />
                  </Link>
                )
              })}
            </div>

            <div className="mt-auto border-t border-warm-gray-100 pt-6 flex flex-col gap-4">
              <div className="flex flex-col gap-2 text-sm text-warm-gray-500 px-4">
                <span className="font-semibold text-warm-gray-800">Kristensen Byg og Montage ApS</span>
                <a href="tel:+4528558925" className="hover:text-brand-green transition-colors whitespace-nowrap">
                  <i className="fa-light fa-phone mr-2 text-brand-green" /> 28 55 89 25
                </a>
                <a href="mailto:kontakt@k-bm.dk" className="hover:text-brand-green transition-colors whitespace-nowrap">
                  <i className="fa-light fa-envelope mr-2 text-brand-green" /> kontakt@k-bm.dk
                </a>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center rounded bg-brand-green py-3 text-base font-semibold uppercase tracking-wider text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200"
              >
                Kontakt os nu
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
